import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './ToastContainer';
import WaitlistModal from './WaitlistModal'; // BETA MODE

const HomeNew: React.FC = () => {
  // Add custom animations
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes float {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        33% { transform: translateY(-10px) rotate(1deg); }
        66% { transform: translateY(-5px) rotate(-1deg); }
      }
      @keyframes glow {
        0%, 100% { box-shadow: 0 0 20px rgba(139, 92, 246, 0.3); }
        50% { box-shadow: 0 0 40px rgba(139, 92, 246, 0.6), 0 0 60px rgba(139, 92, 246, 0.4); }
      }
      @keyframes bounce-slow {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-5px); }
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
  const { isAuthenticated } = useAuth();
  // showError will be needed when handleSubscription is re-enabled
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { showError } = useToast();
  const [scrollY, setScrollY] = useState(0);
  const [currentSection, setCurrentSection] = useState(0);
  const heroRef = useRef<HTMLDivElement>(null);
  const sectionsRef = useRef<(HTMLElement | null)[]>([]);

  // BETA MODE: Waitlist modal state
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);

  // Smooth scroll tracking for storytelling
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      
      // Determine current section for narrative progression
      const windowHeight = window.innerHeight;
      const sections = sectionsRef.current;
      
      sections.forEach((section, index) => {
        if (section) {
          const rect = section.getBoundingClientRect();
          if (rect.top <= windowHeight * 0.3 && rect.bottom >= windowHeight * 0.3) {
            setCurrentSection(index);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for progressive reveals
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const element = entry.target as HTMLElement;
            element.style.opacity = '1';
            element.style.transform = 'translateY(0)';
            element.style.transition = 'opacity 1.2s cubic-bezier(0.16, 1, 0.3, 1), transform 1.2s cubic-bezier(0.16, 1, 0.3, 1)';
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -200px 0px' }
    );

    const elements = document.querySelectorAll('.reveal');
    elements.forEach((el) => {
      const element = el as HTMLElement;
      // Set initial state - simplified for better performance
      element.style.opacity = '0';
      element.style.transform = 'translateY(20px)';
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // BETA MODE: Show waitlist modal on first visit
  useEffect(() => {
    const waitlistShown = localStorage.getItem('waitlistShown');
    if (!waitlistShown && !isAuthenticated) {
      // Show modal after 2 seconds to not be intrusive
      const timer = setTimeout(() => {
        setShowWaitlistModal(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated]);

  // BETA MODE: handleSubscription disabled - all subscription functionality is disabled during beta
  // Uncomment when ready to re-enable subscriptions
  /*
  const handleSubscription = async () => {
    if (!isAuthenticated) {
      navigate('/register');
      return;
    }

    // If user has active subscription, navigate to billing settings
    if (user?.hasActiveSubscription) {
      navigate('/settings?tab=billing');
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();

      if (!url) {
        throw new Error('No checkout URL received from server');
      }

      // Redirect to Stripe checkout
      window.location.href = url;
    } catch (error) {
      console.error('Checkout error:', error);
      showError(error instanceof Error ? error.message : 'Failed to start checkout process');
    } finally {
      setIsLoading(false);
    }
  };
  */

  return (
    <>
    <div className="relative">
      {/* Floating Progress Indicator */}
      <div className="fixed top-1/2 right-8 z-50 transform -translate-y-1/2 hidden lg:flex flex-col space-y-3">
        {['The Problem', 'Our Solution', 'How It Works', 'Pricing'].map((label, index) => (
          <div
            key={index}
            className={`relative group cursor-pointer transition-all duration-300 ${
              currentSection === index ? 'scale-110' : ''
            }`}
            onClick={() => {
              sectionsRef.current[index]?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            <div
              className={`w-3 h-3 rounded-full border-2 transition-all duration-300 ${
                currentSection === index
                  ? 'bg-primary-500 border-primary-500 shadow-lg shadow-primary-500/50'
                  : 'bg-transparent border-gray-400 hover:border-primary-500'
              }`}
            />
            <div className="absolute right-5 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                {label}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Hero Section - The Story Begins */}
      <section
        ref={heroRef}
        className="relative overflow-hidden min-h-screen flex items-start pt-20"
        style={{
          transform: `translateY(${scrollY * 0.3}px)`,
        }}
      >
        {/* Dynamic Background Layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-white/50 to-purple-50/30 dark:from-gray-900/50 dark:via-gray-800/70 dark:to-indigo-900/30"></div>
        <div className="absolute inset-0 backdrop-blur-[1px]"></div>
        
        {/* Animated Gradient Orbs */}
        <div
          className="absolute top-10 sm:top-20 left-10 sm:left-20 w-48 h-48 sm:w-96 sm:h-96 bg-gradient-to-r from-primary-500/15 to-secondary-500/15 rounded-full blur-3xl"
          style={{
            transform: `translate(${scrollY * 0.1}px, ${scrollY * 0.05}px) scale(${1 + scrollY * 0.0001})`,
          }}
        />
        <div
          className="absolute bottom-10 sm:bottom-20 right-10 sm:right-20 w-48 h-48 sm:w-80 md:w-[500px] sm:h-80 md:h-[500px] bg-gradient-to-r from-secondary-500/15 to-primary-500/15 rounded-full blur-3xl"
          style={{
            transform: `translate(${-scrollY * 0.08}px, ${-scrollY * 0.03}px) scale(${1 + scrollY * 0.0002})`,
          }}
        />
        

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-32 pb-24 sm:pb-32">
          <div className="text-center">
            {/* Main Headline - Layered Animation */}
            <div className="relative">
              <h1 
                className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white leading-tight mb-6"
                style={{
                  transform: `translateY(${Math.min(scrollY * 0.05, 10)}px)`,
                  opacity: Math.max(1 - scrollY * 0.001, 0),
                }}
              >
                <span className="block">Stop Losing</span>
                <span className="block bg-gradient-to-r from-primary-500 via-secondary-500 to-primary-600 bg-clip-text text-transparent">
                  Customers After Hours
                </span>
              </h1>
            </div>

            {/* Subtitle with Staggered Animation */}
            <p 
              className="mt-8 text-xl sm:text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed"
              style={{
                transform: `translateY(${Math.min(scrollY * 0.03, 5)}px)`,
                opacity: Math.max(1 - scrollY * 0.0008, 0),
              }}
            >
              Your AI assistant works{' '}
              <span className="font-semibold text-primary-600 dark:text-primary-400">24/7</span> to book appointments, 
              answer questions, and capture leads while you sleep.
            </p>
            
            {/* CTA Buttons with Micro-interactions */}
            <div 
              className="mt-12 flex flex-col sm:flex-row gap-4 justify-center"
              style={{
                transform: `translateY(${Math.min(scrollY * 0.02, 3)}px)`,
                opacity: Math.max(1 - scrollY * 0.0006, 0),
              }}
            >
              {/* BETA MODE: Hero CTA disabled
              <Link
                to={isAuthenticated ? "/dashboard" : "/register"}
                className="group relative inline-flex items-center px-8 py-4 text-lg font-semibold rounded-2xl text-white bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 active:scale-[0.95] transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] shadow-2xl hover:shadow-primary-500/25 hover:scale-[1.02] transform"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-primary-400 to-secondary-400 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-300" />
                <span className="relative flex items-center">
                  {isAuthenticated ? "Go to Dashboard" : "Start Free Trial"}
                  <svg className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </Link> */}
              <button
                disabled={true}
                className="group relative inline-flex items-center px-5 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-2xl text-white bg-gray-400 dark:bg-gray-600 cursor-not-allowed opacity-60 shadow-xl"
              >
                <span className="relative flex items-center">
                  Coming Soon
                  <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </button>
              <a
                href="#story"
                className="group inline-flex items-center px-5 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-medium rounded-2xl text-gray-700 dark:text-gray-200 bg-white/40 dark:bg-gray-800/40 backdrop-blur-lg border border-white/30 dark:border-gray-700/30 hover:bg-white/60 dark:hover:bg-gray-800/60 active:scale-[0.95] hover:scale-[1.05] transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] shadow-xl hover:shadow-2xl"
              >
                <span className="flex items-center">
                  See The Story
                  <svg className="ml-2 w-5 h-5 group-hover:translate-y-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </span>
              </a>
            </div>

            {/* Social Proof Stats with Hover Effects */}
            <div
              className="mt-16 sm:mt-20 grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 text-center"
              style={{
                opacity: Math.max(1 - scrollY * 0.0004, 0),
              }}
            >
              {[
                { value: "5 min", label: "Setup Time", description: "From signup to live chatbot" },
                { value: "24/7", label: "Available", description: "Never miss another customer" },
                { value: "0", label: "Coding Required", description: "Works for non-technical owners" }
              ].map((stat, index) => (
                <div
                  key={index}
                  className="group relative bg-white/30 dark:bg-gray-800/30 backdrop-blur-lg rounded-3xl p-6 sm:p-8 border border-white/40 dark:border-gray-700/40 shadow-xl hover:shadow-2xl hover:bg-white/40 dark:hover:bg-gray-800/40 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] hover:scale-[1.03] hover:-translate-y-2 cursor-pointer"
                  style={{
                    transform: `translateY(${Math.min(scrollY * 0.01, 2)}px)`,
                    animationDelay: `${index * 0.1}s`,
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-secondary-500/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative z-10">
                    <div className="text-4xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">
                      {stat.value}
                    </div>
                    <div className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">
                      {stat.label}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {stat.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Chapter 1: The Problem */}
      <section 
        id="story"
        ref={(el) => { sectionsRef.current[0] = el; }}
        className="relative py-20 lg:py-28 bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900"
      >
        {/* Subtle top border for clean separation */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent"></div>
        
        {/* Clean Background */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(120,119,198,0.1),transparent),radial-gradient(circle_at_80%_80%,rgba(120,119,198,0.1),transparent)]" />
        
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Problem Statement */}
            <div className="reveal opacity-0 translate-y-4">
              <div className="text-red-400 font-semibold mb-4 flex items-center">
                <div className="w-12 h-px bg-red-400 mr-4" />
                Chapter 1: The Problem
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-8 leading-tight">
                Your Business Sleeps,
                <span className="block text-red-500 dark:text-red-400">But Customers Don't</span>
              </h2>
              <div className="space-y-6 text-gray-700 dark:text-gray-300 text-lg leading-relaxed">
                <p>
                  Every night at 10 PM, your phone stops ringing. Your website goes quiet. 
                  But your potential customers? They're still awake, still searching, still ready to book.
                </p>
                <p>
                  <span className="text-gray-900 dark:text-white font-semibold">73% of customers</span> expect businesses 
                  to be available 24/7. When they can't reach you, they call your competitor instead.
                </p>
              </div>
              
              {/* Problem Stats */}
              <div className="mt-12 grid grid-cols-2 gap-8">
                <div className="text-center p-6 bg-red-50 dark:bg-red-900/20 rounded-2xl border border-red-200 dark:border-red-500/30">
                  <div className="text-3xl font-bold text-red-600 dark:text-red-400 mb-2">$2,847</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Avg revenue lost per month</div>
                </div>
                <div className="text-center p-6 bg-red-50 dark:bg-red-900/20 rounded-2xl border border-red-200 dark:border-red-500/30">
                  <div className="text-3xl font-bold text-red-600 dark:text-red-400 mb-2">67%</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Calls go unanswered after hours</div>
                </div>
              </div>
            </div>

            {/* Problem Visualization */}
            <div className="reveal opacity-0 translate-y-4" style={{ animationDelay: '0.8s' }}>
              <div className="relative">
                {/* Phone mockup with missed calls */}
                <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-2xl border border-gray-200 dark:border-gray-700">
                  <div className="bg-white dark:bg-gray-700 rounded-2xl p-4 h-96 relative overflow-hidden border border-gray-200 dark:border-gray-600">
                    <div className="text-gray-900 dark:text-white text-center py-4 border-b border-gray-200 dark:border-gray-600">
                      <div className="text-sm text-gray-500 dark:text-gray-400">After Hours</div>
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">11:47 PM</div>
                    </div>
                    
                    {/* Missed call notifications */}
                    <div className="space-y-3 mt-4">
                      {[
                        { time: "11:45 PM", name: "Sarah M.", action: "Wanted hair appointment" },
                        { time: "11:32 PM", name: "Mike K.", action: "Emergency dental cleaning" },
                        { time: "11:18 PM", name: "Lisa C.", action: "Massage booking" },
                        { time: "10:55 PM", name: "David R.", action: "Consultation request" },
                      ].map((call, index) => (
                        <div 
                          key={index} 
                          className="flex items-center space-x-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border-l-4 border-red-500 shadow-sm"
                          style={{ animationDelay: `${index * 0.5}s` }}
                        >
                          <div className="w-2 h-2 bg-red-500 rounded-full" />
                          <div className="flex-1 text-xs">
                            <div className="text-gray-900 dark:text-white font-medium">{call.name}</div>
                            <div className="text-gray-600 dark:text-gray-300">{call.action}</div>
                          </div>
                          <div className="text-red-600 text-xs font-medium">{call.time}</div>
                        </div>
                      ))}
                    </div>
                    
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chapter 2: The Solution */}
      <section 
        ref={(el) => { sectionsRef.current[1] = el; }}
        className="relative py-20 lg:py-28 bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900"
      >
        {/* Subtle top border for clean separation */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-200 dark:via-primary-700 to-transparent"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="bg-gradient-to-r from-primary-500 to-secondary-500 bg-clip-text text-transparent font-semibold mb-4 flex items-center justify-center">
              <div className="w-12 h-px bg-gray-400 mr-4" />
              Chapter 2: Your AI Superhero
              <div className="w-12 h-px bg-gray-400 ml-4" />
            </div>
            <h2 className="text-5xl sm:text-6xl font-bold text-gray-900 dark:text-white mb-8">
              Meet Your 24/7
              <span className="block text-gray-900 dark:text-white">
                Business Assistant
              </span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              While you sleep, your AI agent works. Answering questions, booking appointments, 
              and turning every visitor into a potential customer.
            </p>
          </div>

          {/* Interactive Demo Timeline */}
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gray-300 dark:bg-gray-600 rounded-full opacity-50" />
            
            {/* Timeline items */}
            <div className="space-y-24">
              {[
                {
                  time: "10:30 PM",
                  title: "Customer visits your website",
                  description: "Sarah finds your salon through Google search",
                  icon: "🌙",
                  side: "left"
                },
                {
                  time: "10:32 PM", 
                  title: "AI instantly engages",
                  description: "Svarra greets Sarah and asks how it can help",
                  icon: "🤖",
                  side: "right"
                },
                {
                  time: "10:35 PM",
                  title: "Books appointment seamlessly", 
                  description: "Sarah books a hair cut for Thursday at 3 PM",
                  icon: "📅",
                  side: "left"
                },
                {
                  time: "10:37 PM",
                  title: "Automatic confirmation",
                  description: "Calendar updated, SMS sent, customer happy",
                  icon: "✅",
                  side: "right"
                }
              ].map((item, index) => (
                <div 
                  key={index}
                  className={`reveal opacity-0 translate-y-4 transition-all duration-700 flex items-center ${
                    item.side === 'left' ? 'lg:justify-start' : 'lg:justify-end'
                  }`}
                  style={{ transitionDelay: `${index * 0.2}s` }}
                >
                  <div className={`relative max-w-md ${item.side === 'right' ? 'lg:mr-8' : 'lg:ml-8'}`}>
                    {/* Timeline dot */}
                    <div className="absolute top-6 left-1/2 lg:left-auto lg:right-full lg:mr-8 transform -translate-x-1/2 lg:translate-x-0 w-4 h-4 bg-primary-500 rounded-full border-4 border-white shadow-lg z-10" />
                    
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border border-gray-200 dark:border-gray-700 hover:shadow-2xl transition-shadow duration-300">
                      <div className="flex items-center mb-4">
                        <span className="text-2xl mr-3">{item.icon}</span>
                        <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                          {item.time}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Chapter 3: How It Works */}
      <section 
        ref={(el) => { sectionsRef.current[2] = el; }}
        className="relative py-20 lg:py-28 bg-gray-50 dark:bg-gray-900"
      >
        {/* Subtle top border for clean separation */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="bg-gradient-to-r from-primary-500 to-secondary-500 bg-clip-text text-transparent font-semibold mb-4 flex items-center justify-center">
              <div className="w-12 h-px bg-gray-400 mr-4" />
              Chapter 3: Simple Setup
              <div className="w-12 h-px bg-gray-400 ml-4" />
            </div>
            <h2 className="text-5xl font-bold text-gray-900 dark:text-white mb-8">
              From Zero to Hero
              <span className="block text-gray-900 dark:text-white">In Under 5 Minutes</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              No technical skills required. If you can fill out a form, you can create an AI agent.
            </p>
          </div>

          {/* Step-by-step cards */}
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {[
              {
                step: "01",
                title: "Tell Us About Your Business",
                description: "Business name, type, services, and preferred tone. Takes 2 minutes.",
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                ),
                color: "from-blue-500 to-primary-500"
              },
              {
                step: "02", 
                title: "Test Your AI Agent",
                description: "Chat with your agent, see how it responds, make tweaks if needed.",
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                ),
                color: "from-primary-500 to-secondary-500"
              },
              {
                step: "03",
                title: "Go Live & Start Booking",
                description: "Share your chat link or embed on your site. Customers start booking instantly.",
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                ),
                color: "from-secondary-500 to-purple-500"
              }
            ].map((item, index) => (
              <div
                key={index}
                className="reveal opacity-0 translate-y-4 transition-all duration-700 group"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className="relative bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] border border-gray-200 dark:border-gray-700 group-hover:border-primary-300 dark:group-hover:border-primary-600 hover:scale-[1.02] hover:-translate-y-2 h-full">
                  {/* Icon */}
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl text-white mb-6 group-hover:scale-[1.05] transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]">
                    {item.icon}
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {item.description}
                  </p>
                  
                  {/* Hover gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-secondary-500/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Chapter 4: Investment in Success */}
      <section 
        ref={(el) => { sectionsRef.current[3] = el; }}
        id="pricing"
        className="relative py-20 lg:py-28 bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900"
      >
        {/* Subtle top border for clean separation */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent"></div>
        
        {/* Dynamic Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-grid-white/5 bg-grid-16"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-primary-500/10 via-transparent to-secondary-500/10"></div>
          {/* Floating Elements */}
          <div className="absolute top-20 left-10 w-32 h-32 bg-primary-400/20 rounded-full blur-2xl animate-bounce delay-1000"></div>
          <div className="absolute bottom-32 right-20 w-48 h-48 bg-secondary-400/20 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header with Value Proposition */}
          <div className="text-center mb-16">
            <div className="bg-gradient-to-r from-primary-500 to-secondary-500 bg-clip-text text-transparent font-semibold mb-4 flex items-center justify-center">
              <div className="w-12 h-px bg-gray-400 mr-4" />
              Chapter 4: Smart Investment
              <div className="w-12 h-px bg-gray-400 ml-4" />
            </div>
            <h2 className="text-4xl sm:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              <span className="block">Your Best</span>
              <span className="block text-gray-900 dark:text-white">
                Business Investment
              </span>
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              Stop losing customers to competitors. Start capturing revenue 24/7.
            </p>
          </div>

          {/* Visual Money Flow */}
          <div className="mb-20">
            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-lg rounded-3xl p-8 border border-gray-200 dark:border-white/10 shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">The Math Is Simple</h3>
              
              {/* Money Flow Animation */}
              <div className="flex flex-col lg:flex-row items-center justify-center space-y-8 lg:space-y-0 lg:space-x-12">
                {/* Your Investment */}
                <div className="text-center">
                  <div className="w-24 h-24 mx-auto mb-4 bg-red-500/20 rounded-full border-2 border-red-400/50 flex items-center justify-center">
                    <span className="text-3xl">💸</span>
                  </div>
                  <div className="text-red-500 dark:text-red-300 text-lg font-semibold">You Pay</div>
                  <div className="text-gray-900 dark:text-white text-2xl font-bold">$250/month</div>
                  <div className="text-gray-600 dark:text-gray-400 text-sm">About $8/day</div>
                </div>

                {/* Arrow */}
                <div className="hidden lg:block">
                  <svg className="w-16 h-8 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>

                {/* Break Even */}
                <div className="text-center">
                  <div className="w-24 h-24 mx-auto mb-4 bg-yellow-500/20 rounded-full border-2 border-yellow-400/50 flex items-center justify-center">
                    <span className="text-3xl">⚖️</span>
                  </div>
                  <div className="text-yellow-600 dark:text-yellow-300 text-lg font-semibold">Break Even</div>
                  <div className="text-gray-900 dark:text-white text-2xl font-bold">3 Customers</div>
                  <div className="text-gray-600 dark:text-gray-400 text-sm">Average: $85 service</div>
                </div>

                {/* Arrow */}
                <div className="hidden lg:block">
                  <svg className="w-16 h-8 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>

                {/* Pure Profit */}
                <div className="text-center">
                  <div className="w-24 h-24 mx-auto mb-4 bg-green-500/20 rounded-full border-2 border-green-400/50 flex items-center justify-center">
                    <span className="text-3xl">💰</span>
                  </div>
                  <div className="text-green-600 dark:text-green-300 text-lg font-semibold">Pure Profit</div>
                  <div className="text-gray-900 dark:text-white text-2xl font-bold">Customer 4+</div>
                  <div className="text-gray-600 dark:text-gray-400 text-sm">Everything after</div>
                </div>
              </div>

              {/* Real Numbers */}
              <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-gradient-to-br from-green-500/10 to-green-600/10 rounded-2xl border border-green-400/20">
                  <div className="text-3xl font-bold text-green-400">$90</div>
                  <div className="text-green-600 dark:text-green-300 text-sm">Monthly profit with 4 customers</div>
                </div>
                <div className="text-center p-6 bg-gradient-to-br from-primary-500/10 to-primary-600/10 rounded-2xl border border-primary-400/20">
                  <div className="text-3xl font-bold text-primary-600 dark:text-primary-400">$260</div>
                  <div className="text-primary-600 dark:text-primary-300 text-sm">Monthly profit with 6 customers</div>
                </div>
                <div className="text-center p-6 bg-gradient-to-br from-secondary-500/10 to-secondary-600/10 rounded-2xl border border-secondary-400/20">
                  <div className="text-3xl font-bold text-secondary-600 dark:text-secondary-400">$3,270</div>
                  <div className="text-secondary-600 dark:text-secondary-300 text-sm">Annual profit with 10 customers</div>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing Card */}
          <div className="max-w-lg mx-auto mb-16">
            <div className="relative">
              {/* Spotlight Effect */}
              <div className="absolute -inset-4 bg-gradient-to-r from-primary-500/20 via-secondary-500/20 to-primary-500/20 rounded-3xl blur-xl"></div>
              
              <div className="relative bg-white/90 dark:bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-gray-200 dark:border-white/20 shadow-xl">
                {/* Badge */}
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-green-400 to-green-500 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg text-center">
                    One Plan, Unlimited Possibilities
                  </div>
                </div>
                
                {/* Price */}
                <div className="text-center mt-4 mb-8">
                  <div className="flex items-baseline justify-center mb-2">
                    <span className="text-6xl font-bold text-gray-900 dark:text-white">$250</span>
                    <span className="text-xl text-gray-600 dark:text-gray-300 ml-2">/month</span>
                  </div>
                  <div className="text-gray-600 dark:text-gray-400">Everything included • Cancel anytime</div>
                </div>

                {/* Features */}
                <div className="space-y-4 mb-8">
                  {[
                    { text: "Up to 5 AI agents", popular: true },
                    { text: "24/7 automated responses", popular: true },
                    { text: "Appointment booking system", popular: true },
                    { text: "Google Calendar integration", popular: false },
                    { text: "Conversation analytics", popular: false },
                    { text: "Email & SMS notifications", popular: false },
                    { text: "Custom business prompts", popular: false },
                    { text: "30-day money-back guarantee", popular: false }
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className={`${feature.popular ? 'text-gray-900 dark:text-white font-semibold' : 'text-gray-700 dark:text-gray-300'}`}>
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>


                {/* CTA Button */}
                {/* ========== BETA MODE: SUBSCRIPTION DISABLED ==========
                    To re-enable subscriptions, uncomment the block below and remove the temporary button
                <button
                  onClick={handleSubscription}
                  disabled={isLoading}
                  className="w-full group relative inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-2xl text-white bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-400 hover:to-secondary-400 active:scale-[0.95] transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] shadow-2xl hover:shadow-primary-500/25 hover:scale-[1.05] transform disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-400 to-secondary-400 rounded-2xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>

                  <span className="relative flex items-center">
                    {isLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        {isAuthenticated
                          ? (user?.hasActiveSubscription
                              ? 'Manage Subscription'
                              : user?.isEligibleForTrial
                                ? 'Start Free Trial'
                                : 'Subscribe Now')
                          : 'Start 14-Day Free Trial'}
                        <svg className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </>
                    )}
                  </span>
                </button>
                ========== END BETA MODE ========== */}

                {/* BETA MODE: Temporary disabled button */}
                <button
                  disabled={true}
                  className="w-full group relative inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-2xl text-white bg-gray-400 dark:bg-gray-600 cursor-not-allowed opacity-60 shadow-2xl"
                >
                  <span className="relative flex items-center">
                    Coming Soon
                    <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                </button>

                <div className="text-center mt-4">
                  {/* BETA MODE: Original text
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    Join businesses already growing with Svarra
                  </p> */}
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    🎉 <strong className="text-gray-900 dark:text-white">Beta is FREE until December 30th!</strong> Join the waitlist to get early access.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Signals */}
          <div className="text-center">
            <div className="flex flex-wrap justify-center items-center gap-8 text-gray-600 dark:text-gray-400 text-sm">
              <div className="flex items-center space-x-2">
                <span>🔒</span>
                <span>SSL Secured</span>
              </div>
              <div className="flex items-center space-x-2">
                <span>💳</span>
                <span>Secure Payments</span>
              </div>
              <div className="flex items-center space-x-2">
                <span>📞</span>
                <span>24/7 Support</span>
              </div>
              <div className="flex items-center space-x-2">
                <span>✨</span>
                <span>Cancel Anytime</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Epilogue: Final Questions */}
      <section id="faq" className="relative py-24 lg:py-32 bg-gradient-to-br from-gray-50 via-white to-primary-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900">
        {/* Subtle top border for clean separation */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-200 dark:via-primary-700 to-transparent"></div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="text-gray-600 dark:text-gray-400 font-semibold mb-4 flex items-center justify-center">
              <div className="w-12 h-px bg-gray-400 mr-4" />
              Questions & Answers
              <div className="w-12 h-px bg-gray-400 ml-4" />
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-8">
              Everything You Need to Know
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Common questions from business owners just like you
            </p>
          </div>

          <div className="grid gap-8 lg:gap-12">
            {[
              {
                question: "How long does it take to set up?",
                answer: "Setup takes less than 5 minutes. Just fill out your business information, and your AI agent is ready to start chatting with customers."
              },
              {
                question: "Do I need any technical knowledge?",
                answer: "Not at all! Svarra is designed for business owners, not developers. If you can fill out a form, you can create an AI agent."
              },
              {
                question: "Can customers really book appointments automatically?",
                answer: "Yes! When you connect your Google Calendar, customers can book appointments by simply chatting with your AI agent. No additional apps or logins required."
              },
              {
                question: "What types of businesses work best?",
                answer: "Svarra is perfect for appointment-based businesses like salons, barbershops, spas, auto-detailing, and any service business that takes bookings."
              },
              {
                question: "Can I customize how my agent responds?",
                answer: "Absolutely! You can set your agent's tone (friendly, professional, casual), add your specific services, policies, and FAQs to make it truly yours."
              },
              {
                question: "Is there really a free trial?",
                answer: "Yes! Get a 14-day free trial with full access to all features. After the trial, your subscription automatically starts at $250/month. Cancel anytime during the trial with no charges."
              }
            ].map((faq, index) => (
              <div 
                key={index} 
                className="reveal opacity-0 translate-y-4 transition-all duration-700 group"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-lg rounded-3xl p-8 border border-gray-200/50 dark:border-gray-700/50 shadow-lg hover:shadow-2xl group-hover:bg-white/80 dark:group-hover:bg-gray-800/80 hover:scale-[1.01] transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] relative overflow-hidden">
                  {/* Gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-secondary-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl" />
                  
                  <div className="relative z-10">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center text-white font-bold group-hover:scale-[1.05] transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]">
                        Q{index + 1}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">
                          {faq.question}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-lg">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Final CTA */}
          <div className="text-center mt-20">
            <div className="bg-gradient-to-r from-primary-500/10 to-secondary-500/10 rounded-3xl p-12 border border-primary-200/30 dark:border-primary-800/30 backdrop-blur-sm">
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                Never Want to Miss Another Customer?
              </h3>
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
                Join 1000+ business owners who transformed their customer service with AI
              </p>
              {/* BETA MODE: Final CTA disabled
              <Link
                to={isAuthenticated ? "/dashboard" : "/register"}
                className="group inline-flex items-center px-8 py-4 text-lg font-semibold rounded-2xl text-white bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 active:scale-[0.98] transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] shadow-xl hover:shadow-primary-500/25 hover:scale-[1.02] transform"
              >
                <span className="flex items-center">
                  {isAuthenticated ? "Go to Dashboard" : "Start Your Free Trial"}
                  <svg className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </Link> */}
              <button
                disabled={true}
                className="group inline-flex items-center px-8 py-4 text-lg font-semibold rounded-2xl text-white bg-gray-400 dark:bg-gray-600 cursor-not-allowed opacity-60 shadow-xl"
              >
                <span className="flex items-center">
                  Coming Soon
                  <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

    </div>

      {/* BETA MODE: Waitlist Modal */}
      <WaitlistModal
        isOpen={showWaitlistModal}
        onClose={() => setShowWaitlistModal(false)}
      />
    </>
  );
};

export default HomeNew;